import React, { useEffect, useState, useRef } from 'react';
import { buildCartUrl, CONFIG } from '../../config';
interface ButtonProps extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'onClick'> {
  children: React.ReactNode;
  testId?: string;
  className?: string;
  withBump?: boolean;
  scrollTo?: string;
  onClick?: (e: React.MouseEvent<HTMLButtonElement>) => void;
}
/**
 * Button component — reads everything from config.ts.
 *
 * Props:
 * - withBump: true = build cart URL with both products + discount code
 * - scrollTo: element ID to smooth-scroll to instead of navigating
 */
export function Button({
  children,
  testId,
  className = '',
  withBump = false,
  scrollTo,
  onClick,
  ...props
}: ButtonProps) {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [inView, setInView] = useState(false);
  // Only run the glow animation when the button is visible on screen.
  // This prevents 4-5 buttons from animating simultaneously and crashing the tab.
  useEffect(() => {
    if (!wrapperRef.current) return;
    const observer = new IntersectionObserver(([entry]) => setInView(entry.isIntersecting), {
      threshold: 0.1
    });
    observer.observe(wrapperRef.current);
    return () => observer.disconnect();
  }, []);
  const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (scrollTo) {
      const el = document.getElementById(scrollTo);
      if (el) el.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
      if (onClick) onClick(e);
      return;
    }
    // Fire Meta Pixel AddToCart + InitiateCheckout
    if (typeof window !== 'undefined' && (window as any).fbq) {
      const value = withBump ? CONFIG.product.price + CONFIG.bump.bumpPrice : CONFIG.product.price;
      const contentIds = withBump ? ['ai-income-blueprint', '50-power-prompts'] : ['ai-income-blueprint'];
      const contentName = withBump ? `${CONFIG.product.name} + ${CONFIG.bump.name}` : CONFIG.product.name;
      (window as any).fbq('track', 'AddToCart', {
        content_name: contentName,
        content_ids: contentIds,
        content_type: 'product',
        value,
        currency: CONFIG.tracking.currency
      });
      (window as any).fbq('track', 'InitiateCheckout', {
        content_name: contentName,
        content_ids: contentIds,
        content_type: 'product',
        value,
        currency: CONFIG.tracking.currency,
        num_items: withBump ? 2 : 1
      });
    }
    const url = buildCartUrl(withBump);
    if (onClick) onClick(e);
    if (!e.defaultPrevented) {
      window.location.href = url;
    }
  };
  return <div ref={wrapperRef} className="relative inline-block" data-id="element-205">
      {/* Pulsing neon glow — only animates when in view */}
      {inView && <div aria-hidden="true" className="btn-glow-layer absolute -inset-1.5 rounded-[14px] pointer-events-none" style={{
      background: 'radial-gradient(ellipse, rgba(250,255,0,0.35) 0%, rgba(250,255,0,0) 70%)',
      filter: 'blur(10px)'
    }} data-id="element-206" />}

      <button data-testid={testId} onClick={handleClick} className={`
          relative bg-brand text-black font-archivo uppercase tracking-tight
          px-8 py-4 rounded-[10px] text-lg sm:text-xl
          transition-all duration-200 ease-out
          hover:-translate-y-[1px] hover:shadow-glow
          active:translate-y-[1px]
          ${className}
        `} {...props} data-id="element-207">
        {children}
      </button>
    </div>;
}